import {readString} from 'react-papaparse';

import {
    BROKEN_TEMPLATE_MESSAGE,
    FIELD,
    HEADERS,
    MAX_RECORD_SIZE,
    MISSING_EMAIL_COLUMN_MESSAGE,
    MISSING_HEADER_ROW_MESSAGE,
} from 'src/modules/create-user/constants';

import type {UserBulkImportFileType} from 'src/modules/create-user/user-bulk-import/typedef';

type ParsedCSVData = {
    [key: string]: string | undefined,
};

export const parseCSVToUserBulkImportType = (file: File): Promise<UserBulkImportFileType[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = event => {
            if (event.target) {
                const fileContent = event.target.result as string;
                readString(fileContent, {
                    header: true,
                    dynamicTyping: true,
                    delimiter: ',',
                    skipEmptyLines: true,
                    quoteChar: '"',
                    preview: MAX_RECORD_SIZE,
                    complete: results => {
                        if (results.errors.length > 0) {
                            reject(results.errors);
                        } else {
                            const normalizedData = (results.data as ParsedCSVData[]).map((item: ParsedCSVData) => {
                                const normalizedItem: UserBulkImportFileType = {
                                    email: String(item[HEADERS[FIELD.EMAIL].toLowerCase()] ?? '').trim(),
                                    firstName: String(item[HEADERS[FIELD.FIRST_NAME].toLowerCase()] ?? '').trim(),
                                    lastName: String(item[HEADERS[FIELD.LAST_NAME].toLowerCase()] ?? '').trim(),
                                    jobTitle: String(item[HEADERS[FIELD.JOB_TITLE].toLowerCase()] ?? '').trim(),
                                    group: String(item[HEADERS[FIELD.GROUP].toLowerCase()] ?? '').trim(),
                                    country: String(item[HEADERS[FIELD.COUNTRY].toLowerCase()] ?? '').trim(),
                                    city: String(item[HEADERS[FIELD.CITY].toLowerCase()] ?? '').trim(),
                                    newsletters: String(item[HEADERS[FIELD.NEWSLETTERS].toLowerCase()] ?? '').trim(),
                                    notes: String(item[HEADERS[FIELD.NOTES].toLowerCase()] ?? '').trim(),
                                };
                                return normalizedItem;
                            });

                            resolve(normalizedData);
                        }
                    },
                    transformHeader: header => header.toLowerCase().trim(),
                });
            } else {
                reject(new Error('Event target is null.'));
            }
        };

        reader.onerror = error => {
            reject(error);
        };

        reader.readAsText(file);
    });
};

export const validateBulkImportFile = (file: File): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = event => {
            if (event.target) {
                const fileContent = event.target.result as string;
                readString(fileContent, {
                    header: false,
                    dynamicTyping: true,
                    delimiter: ',',
                    skipEmptyLines: true,
                    quoteChar: '"',
                    preview: MAX_RECORD_SIZE,
                    complete: results => {
                        if (results.errors.length > 0) {
                            reject(new Error(BROKEN_TEMPLATE_MESSAGE));
                        } else {
                            const rows = results.data as string[][];
                            const headerRow = rows[0] ?? [];
                            const headers = headerRow
                                .filter(Boolean)
                                .map(header => String(header ?? '').toLowerCase().trim());

                            const knownHeaders = Object.values(HEADERS).map(header => header.toLowerCase());
                            const hasAnyKnownHeader = headers.some(header => knownHeaders.includes(header));
                            if (!headers.length || !hasAnyKnownHeader) {
                                reject(new Error(MISSING_HEADER_ROW_MESSAGE));
                                return;
                            }

                            const requiredFields = [
                                HEADERS[FIELD.EMAIL], HEADERS[FIELD.FIRST_NAME], HEADERS[FIELD.LAST_NAME],
                            ];

                            const emailHeader = HEADERS[FIELD.EMAIL].toLowerCase();
                            if (!headers.includes(emailHeader)) {
                                reject(new Error(MISSING_EMAIL_COLUMN_MESSAGE));
                                return;
                            }

                            const emailColumnIndex = headers.indexOf(emailHeader);
                            const hasEmailValues = rows.slice(1).some(row => {
                                const value = row[emailColumnIndex];
                                return typeof value === 'string'
                                    ? value.trim()
                                    : value !== null && value !== undefined && String(value).trim();
                            });
                            if (!hasEmailValues) {
                                reject(new Error(MISSING_EMAIL_COLUMN_MESSAGE));
                                return;
                            }

                            for (const field of requiredFields) {
                                if (!headers.includes(field.toLowerCase())) {
                                    reject(new Error(BROKEN_TEMPLATE_MESSAGE));
                                    return;
                                }
                            }

                            if (results.data.length > (MAX_RECORD_SIZE + 1)) {
                                reject(new Error(`The file contains more than ${MAX_RECORD_SIZE} records.`));
                            } else {
                                resolve(true);
                            }
                        }
                    },
                });
            } else {
                reject(new Error('Event target is null.'));
            }
        };

        reader.onerror = error => {
            reject(error);
        };

        reader.readAsText(file);
    });
};
